import { autocompleteClasses, inputBaseClasses, Select, SelectOption, styled } from '@chronosphereio/chrono-ui';
import { DurationString } from '@chronosphereio/core';
import { useMemo } from 'react';

interface RefreshIntervalPickerProps {
  options?: DurationString[];
  value?: DurationString | null;
  onChange: (value: DurationString | undefined) => void;
}

const OFF_VALUE = 'Off';
// Note: We want to ensure 30s is the minimum value in the list of options to ensure we don't overwhelm our query
// services. Eventually, we may want to add an "Auto" interval that will take in the current time range and set the refresh
export const REFRESH_INTERVAL_DEFAULT_OPTIONS: DurationString[] = ['30s', '1m', '5m', '10m', '30m', '1h'];

function isRefreshOff(value?: SelectOption | undefined) {
  return !value || value.key === OFF_VALUE;
}

export function RefreshIntervalPicker(props: RefreshIntervalPickerProps) {
  const { value, onChange, options = REFRESH_INTERVAL_DEFAULT_OPTIONS } = props;

  const currentValue: SelectOption = useMemo(() => {
    return {
      key: value || OFF_VALUE,
      label: value || OFF_VALUE,
    };
  }, [value]);

  const allOptions: SelectOption[] = useMemo(() => {
    return [OFF_VALUE, ...options].map((opt) => {
      return {
        key: opt,
        label: opt,
      };
    });
  }, [options]);

  return (
    <SelectWrapper
      options={allOptions}
      value={currentValue}
      popperPlacement="bottom-end"
      popperWidth="65px"
      disableClearable
      InputProps={{
        'aria-label': `Select refresh interval. Currently set to ${value}`,
      }}
      isOptionEqualToValue={(option, value) => {
        return option.key === value.key;
      }}
      onChange={(event, value) => {
        if (!value || value.key === OFF_VALUE) {
          onChange(undefined);
          return;
        }
        const duration = value.key as DurationString;
        onChange(duration);
      }}
    />
  );
}

export const SelectWrapper = styled(Select<SelectOption, true, false>)(({ value }) => {
  const isOff = isRefreshOff(value);

  return {
    [`.${inputBaseClasses.root}`]: {
      // Make the input a little smaller to match designs.
      paddingRight: '24px !important',
    },
    [`&.${autocompleteClasses.root} .${autocompleteClasses.endAdornment}`]: {
      // Center input adornment when off.
      left: isOff ? 'calc(50% - 14px)' : undefined,
      right: isOff ? 0 : '4px',
    },
    [`.${autocompleteClasses.inputRoot} .${inputBaseClasses.input}`]: {
      paddingLeft: 0,
      textAlign: 'center',

      // Shrink the input when off. Otherwise, behave as normal to show the
      // selected value.
      minWidth: isOff ? 0 : undefined,
      padding: isOff ? 0 : undefined,
    },
  };
});
